import { useNavigate } from 'react-router-dom';
import { GiftcardListItem } from '../../../interfaces/giftcard.interface';
import { GIFTCARD_FETCH_LIMIT, numberFormat } from '../utils/dashboardData';

/**
 * Un bon valide est un engagement : quelqu'un peut se présenter avec, sans
 * prévenir. Cette dette n'est visible nulle part sans ouvrir la page Bons
 * cadeaux, et elle se compte exactement en un seul appel.
 *
 * Le composant ne connaît pas le rôle de l'utilisateur : c'est le parent qui ne
 * le monte pas pour un non-admin.
 */

/** Horizon « expire bientôt ». Assez court pour qu'un bon dessus soit une nouvelle. */
const SOON_MS = 30 * 24 * 60 * 60 * 1000;

interface Props {
  items: GiftcardListItem[];
  loading: boolean;
  error: string | null;
}

const GiftcardPanel = ({ items, loading, error }: Props) => {
  const navigate = useNavigate();
  const now = Date.now();

  // Formule reprise telle quelle du StatusBadge de GiftcardHistory : les deux
  // pages ne doivent pas pouvoir diverger sur ce qu'est un bon valide.
  const valid = items.filter(
    (item) =>
      !item.used &&
      !(item.expiresAt && new Date(item.expiresAt).getTime() < now)
  );
  const soon = valid.filter(
    (item) => item.expiresAt && new Date(item.expiresAt).getTime() < now + SOON_MS
  ).length;

  return (
    <section className="rounded-2xl border border-[#623413]/10 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-6 py-4">
        <h3 className="font-semibold text-[#623413]">Bons cadeaux</h3>
      </div>

      <div className="space-y-3 px-6 py-4">
        {loading ? (
          <>
            <div className="h-3 w-32 rounded bg-shimmer bg-[length:400%_100%] animate-shimmer" />
            <div className="h-7 w-16 rounded bg-shimmer bg-[length:400%_100%] animate-shimmer" />
            <div className="h-3 w-40 rounded bg-shimmer bg-[length:400%_100%] animate-shimmer" />
          </>
        ) : error ? (
          // Le panneau échoue seul : le reste de la page reste intact.
          <p className="text-sm text-[#BB0F33]">{error}</p>
        ) : (
          <>
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Bons en circulation
            </p>
            <p className="text-2xl font-semibold text-[#623413]">
              {numberFormat.format(valid.length)}
            </p>
            <p
              className={`text-sm ${soon > 0 ? 'text-amber-700' : 'text-gray-600'}`}
            >
              {soon > 0
                ? `${soon} expire${soon > 1 ? 'nt' : ''} sous 30 jours`
                : "Aucun n'expire sous 30 jours"}
            </p>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-6 py-3">
        {/* Plafond réel de l'API, dit honnêtement plutôt que passé sous silence. */}
        <span className="text-xs text-gray-400">
          sur les {GIFTCARD_FETCH_LIMIT} derniers bons émis
        </span>
        <button
          type="button"
          onClick={() => navigate('/giftcards')}
          className="text-sm text-[#623413] hover:underline"
        >
          Voir les bons
        </button>
      </div>
    </section>
  );
};

export default GiftcardPanel;
